/**
 * TAS Command — Sistema Digitale di Gestione Operazioni SAR
 *
 * Copyright (c) 2026 Matteo MESSINA, Alfonso CECERE
 * Tutti i diritti riservati.
 *
 * Sviluppato dagli autori a titolo personale, al di fuori
 * dell'orario di servizio e senza utilizzo di risorse o
 * strumentazione dell'amministrazione di appartenenza.
 *
 * Qualsiasi riproduzione, distribuzione o utilizzo non
 * autorizzato è vietato ai sensi della L. 633/1941.
 *
 * Autori:
 *   Matteo MESSINA  — VE, Comando VVF Benevento, Servizio TAS
 *   Alfonso CECERE  — VE, Comando VVF Benevento, Servizio TAS
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { X, Search } from "lucide-react";

// Normalizzazione COGNOME / NOME usata ovunque si inseriscano dati anagrafici
// (soccorritori, profilo utente, auto-registrazione QR):
//  - cognome → sempre tutto MAIUSCOLO (qualsiasi formato in input);
//  - nome    → prima lettera maiuscola, resto minuscolo per ogni parola (anche
//    dopo spazio/apostrofo/trattino): "maria luisa" → "Maria Luisa".
export const fmtCognome = (s) => (s || "").toUpperCase();
export const fmtNome = (s) => (s || "").toLowerCase().replace(/(^|[\s'-])([a-zà-ÿ])/g, (m, p, c) => p + c.toUpperCase());

// Librerie export pesanti caricate on-demand (dynamic import → chunk separati,
// fuori dal bundle principale): jspdf, jspdf-autotable, xlsx.
export const loadXLSX = () => import("xlsx");
export const loadPdf = async () => {
  const [{ jsPDF }, at] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  return { jsPDF, autoTable: at.default };
};

// Guardia anti-injection testo: su alcuni Android, app/estensioni di terze
// parti (adware, VPN gratuite) sostituiscono nel DOM parole di label statiche
// con testo pubblicitario random (es. "Campania" → "Amalfi"). React non
// ripristina questi nodi da solo perché non rientrano nel suo ciclo di diff
// (il valore renderizzato non cambia, quindi React non riscrive il DOM).
// Questo hook osserva l'elemento e ripristina il testo atteso se alterato
// dall'esterno. Applicato solo a label critiche segnalate, non a tutta l'app.
export const useTextGuard = (ref, expectedText) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !expectedText) return;
    const observer = new MutationObserver(() => {
      if (el.textContent !== expectedText) {
        observer.disconnect();
        el.textContent = expectedText;
        observer.observe(el, { characterData: true, childList: true, subtree: true });
      }
    });
    if (el.textContent !== expectedText) el.textContent = expectedText;
    observer.observe(el, { characterData: true, childList: true, subtree: true });
    return () => observer.disconnect();
  }, [ref, expectedText]);
};

// Variante per <select>: ripristina il testo delle singole <option> se
// alterato (stesso fenomeno di ad-injection osservato sul menù Regione).
export const useSelectOptionsGuard = (ref, expectedLabels) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !expectedLabels?.length) return;
    const restore = () => {
      el.querySelectorAll("option").forEach((opt, i) => {
        const expected = expectedLabels[i];
        if (expected != null && opt.textContent !== expected) opt.textContent = expected;
      });
    };
    const observer = new MutationObserver(() => {
      observer.disconnect();
      restore();
      observer.observe(el, { characterData: true, childList: true, subtree: true });
    });
    restore();
    observer.observe(el, { characterData: true, childList: true, subtree: true });
    return () => observer.disconnect();
  }, [ref, expectedLabels]);
};

// Variante non-hook di useTextGuard, per elementi generati in un .map() dove
// non si possono chiamare hook per-iterazione. Usare come ref callback:
// ref={(el) => attachTextGuard(el, "Chiudi")}
export const attachTextGuard = (el, expectedText) => {
  if (!el || !expectedText || el._textGuardObserver) return;
  const observer = new MutationObserver(() => {
    if (el.textContent !== expectedText) {
      observer.disconnect();
      el.textContent = expectedText;
      observer.observe(el, { characterData: true, childList: true, subtree: true });
    }
  });
  if (el.textContent !== expectedText) el.textContent = expectedText;
  observer.observe(el, { characterData: true, childList: true, subtree: true });
  el._textGuardObserver = observer;
};

// ── Componente riutilizzabile FilterBar ─────────────────────────────────────
// ── Componente header istituzionale per stampa ───────────────────────────────
const PrintHeader = ({ logo, titolo, operazione, nomeSistema }) => (
  <div className="print-header" style={{ display: 'none', marginBottom: '20px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', paddingBottom: '12px', borderBottom: '3px solid #1e293b', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        {logo
          ? <img src={logo} alt="Logo" style={{ height: '56px', width: 'auto', objectFit: 'contain' }} />
          : <div style={{ width: '52px', height: '52px', background: '#f97316', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '900', fontSize: '16px', letterSpacing: '-0.5px' }}>TAS</div>
        }
        <div>
          <div style={{ fontSize: '20px', fontWeight: '900', color: '#0f172a', letterSpacing: '-0.5px' }}>{nomeSistema || 'TAS Command'}</div>
          <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sistema di Gestione Operazioni TAS</div>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', marginBottom: '6px' }}>{titolo}</div>
        {/* Un report di esercitazione non deve essere confondibile con uno di
            intervento reale: la dicitura è ben visibile in testa al documento. */}
        {operazione?.tipo === 'ESERCITAZIONE' && (
          <div style={{ fontSize: '12px', fontWeight: '900', color: '#b45309', background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '3px', padding: '2px 8px', display: 'inline-block', marginBottom: '5px', letterSpacing: '0.08em' }}>
            ESERCITAZIONE
          </div>
        )}
        {operazione && (
          <div style={{ fontSize: '11px', color: '#374151' }}>
            <span style={{ fontWeight: '600' }}>{operazione.nome}</span>
            {operazione.localita ? ` · ${operazione.localita}` : ''}
            {operazione.regione ? ` · ${operazione.regione}` : ''}
          </div>
        )}
        {operazione?.data_inizio && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>Data operazione: {operazione.data_inizio}</div>}
        <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '6px', borderTop: '1px solid #f3f4f6', paddingTop: '4px' }}>
          Stampato il {new Date().toLocaleDateString('it-IT')} alle {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  </div>
);

const FilterBar = ({ filters, onChange, onReset, total, shown, active, children }) => (
  <div className="tactical-card p-3 mb-4 print-hide">
    <div className="flex flex-wrap gap-2 items-end">
      {children}
      <button
        className="btn btn-secondary btn-sm py-1 px-3 text-xs flex items-center gap-1 self-end"
        onClick={onReset}
        title="Azzera tutti i filtri"
      >
        <X size={12} /> Azzera
      </button>
    </div>
    {total > 0 && (
      <p className="text-xs text-slate-500 mt-2">
        Mostrando <span className="text-white font-medium">{shown}</span> di <span className="text-white font-medium">{total}</span> risultati
        {active != null && <> di cui <span className="text-emerald-400 font-medium">{active}</span> attive</>}
      </p>
    )}
  </div>
);

const FilterInput = ({ label, value, onChange, placeholder, type = "text", className = "" }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    {label && <label className="text-xs text-slate-400">{label}</label>}
    <div className="relative">
      {type === "text" && <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />}
      <input
        type={type}
        className={`form-input py-1 text-xs ${className}`}
        style={{ minWidth: 140, paddingLeft: type === "text" ? "2rem" : undefined }}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  </div>
);

const FilterSelect = ({ label, value, onChange, options, placeholder = "Tutti", className = "" }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    {label && <label className="text-xs text-slate-400">{label}</label>}
    <select
      className="form-select py-1 text-xs"
      style={{ minWidth: 130 }}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      <option value="">{placeholder}</option>
      {options.map(o => (
        <option key={typeof o === 'string' ? o : o.value} value={typeof o === 'string' ? o : o.value}>
          {typeof o === 'string' ? o : o.label}
        </option>
      ))}
    </select>
  </div>
);
// ─────────────────────────────────────────────────────────────────────────────

// ── Hook: debounce su input testuali (evita ricalcoli ad ogni tasto) ─────────
const useDebounce = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// ── Hook: paginazione lato client ────────────────────────────────────────────
const usePagination = (data, pageSize = 50) => {
  const [page, setPage] = useState(1);
  const total = data.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), pages);
  // Torna a pag.1 quando cambia il numero di risultati (filtro applicato)
  useEffect(() => { setPage(1); }, [total]);
  return {
    page: safePage, pages, total, pageSize, setPage,
    current: data.slice((safePage - 1) * pageSize, safePage * pageSize),
  };
};

// ── Componente Paginazione ────────────────────────────────────────────────────
const Pagination = ({ page, pages, total, pageSize, setPage }) => {
  if (pages <= 1) return null;
  const from = (page - 1) * pageSize + 1;
  const to   = Math.min(page * pageSize, total);
  const nums = [];
  for (let p = Math.max(1, page - 2); p <= Math.min(pages, page + 2); p++) nums.push(p);
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-700 bg-slate-900/50 rounded-b-lg">
      <span className="text-xs text-slate-400">
        Mostrando <span className="text-white font-medium">{from}–{to}</span> di <span className="text-white font-medium">{total}</span>
      </span>
      <div className="flex items-center gap-1">
        <button className="pag-btn" onClick={() => setPage(1)} disabled={page === 1} title="Prima pagina">«</button>
        <button className="pag-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1} title="Pagina precedente">‹</button>
        {nums[0] > 1 && <span className="text-slate-500 text-xs px-1">…</span>}
        {nums.map(p => <button key={p} className={`pag-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
        {nums[nums.length - 1] < pages && <span className="text-slate-500 text-xs px-1">…</span>}
        <button className="pag-btn" onClick={() => setPage(p => p + 1)} disabled={page === pages} title="Pagina successiva">›</button>
        <button className="pag-btn" onClick={() => setPage(pages)} disabled={page === pages} title="Ultima pagina">»</button>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

// Validazione coordinate
const validateLatLon = (lat, lon) => {
  let latErr = "", lonErr = "";
  if (lat !== "" && lat !== null && lat !== undefined) {
    const v = parseFloat(lat);
    if (isNaN(v) || v < -90 || v > 90) latErr = "Latitudine non valida (range -90/+90)";
  }
  if (lon !== "" && lon !== null && lon !== undefined) {
    const v = parseFloat(lon);
    if (isNaN(v) || v < -180 || v > 180) lonErr = "Longitudine non valida (range -180/+180)";
  }
  return { latErr, lonErr };
};

// Parsa una stringa "lat, lon" in { lat, lon } (stringhe) e valida
const parseCoordPair = (str) => {
  if (!str || !str.trim()) return { lat: '', lon: '' };
  const parts = str.split(',').map(s => s.trim());
  if (parts.length >= 2) return { lat: parts[0], lon: parts[1] };
  return { lat: str.trim(), lon: '' };
};
/** Rimuove dall'input coordinate qualsiasi carattere non valido (solo cifre, punto, virgola, spazio, segno) */
const filterCoord = (v) => v.replace(/[^0-9.,\s\-+]/g, '');

const validateCoordStr = (str) => {
  if (!str || !str.trim()) return null;
  const { lat, lon } = parseCoordPair(str);
  const latN = parseFloat(lat), lonN = parseFloat(lon);
  if (isNaN(latN) || isNaN(lonN)) return "Formato non valido (es. 41.123456, 14.567890)";
  if (latN < -90 || latN > 90) return "Latitudine fuori range (-90/+90)";
  if (lonN < -180 || lonN > 180) return "Longitudine fuori range (-180/+180)";
  return null;
};

// ── Chiave della cache locale del meteo (condivisa fra le pagine) ────────────
const chiaveMeteoCache = (operazioneId) => `tas:meteo:${operazioneId}`;

/** Ultimo meteo acquisito per l'operazione, o null. Non fa rete. */
const meteoInCache = (operazioneId) => {
  if (!operazioneId) return null;
  try { return JSON.parse(localStorage.getItem(chiaveMeteoCache(operazioneId)) || 'null'); }
  catch { return null; }
};

// Genera il nome turno automatico nel formato aaaammgg_Giorno|Notte.
// GIORNO = ora attuale >= alba E < tramonto.
// NOTTE  = tutto il resto.
//
// Due fonti per alba e tramonto, in ordine di precisione:
//   1. `orarioSole` — alba/tramonto REALI del giorno sulle coordinate del PCA
//      (da Open-Meteo). Disponibile solo per un'operazione gia' aperta.
//   2. `effemeridi` — medie MENSILI dal file statico. Meno precise (una ventina
//      di minuti nel caso peggiore) ma non richiedono ne' rete ne' coordinate:
//      servono quando si sta CREANDO l'operazione, dove il PCA puo' ancora non
//      esserci, e ad app aperta a freddo senza campo.
// Senza nessuna delle due: "Giorno" come default.
const getNomeTurnoAuto = (effemeridi = null, orarioSole = null) => {
  const now  = new Date();
  const anno = String(now.getFullYear());
  const pad  = n => String(n).padStart(2, "0");
  const dateStr    = `${anno}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const oraAttuale = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  let tipo = "Giorno";
  if (orarioSole?.alba && orarioSole?.tramonto) {
    tipo = (oraAttuale >= orarioSole.alba && oraAttuale < orarioSole.tramonto) ? "Giorno" : "Notte";
  } else if (effemeridi) {
    const mese = String(now.getMonth() + 1);
    const annoData = effemeridi[anno] ?? effemeridi[Object.keys(effemeridi)[0]];
    const meseData = annoData?.[mese];
    if (meseData) {
      const { alba, tramonto } = meseData;
      tipo = (oraAttuale >= alba && oraAttuale < tramonto) ? "Giorno" : "Notte";
    }
  }
  return `${dateStr}_${tipo}`;
};

// ── Selezione multipla per azioni massive ────────────────────────────────────
// Modello: la selezione dei soccorritori. Sta qui perché le tabelle che la usano
// sono quattro (Utenti, Personale TAS2, Sedi VVF, Enti) e la parte che si sbaglia
// è sempre la stessa, non la casella: è il rapporto fra selezione e filtro.
//
// Due regole, entrambe imparate da quel caso:
//  - "seleziona tutti" vuol dire **tutte quelle che si vedono adesso**, non
//    tutte quelle caricate. Agire in massa su righe fuori dal filtro corrente
//    significa agire su righe che non si hanno davanti;
//  - una riga sparita (cancellata, o ricaricata via) esce dalla selezione da
//    sola. Altrimenti resta un id fantasma che al prossimo comando produce un
//    errore per qualcosa che l'operatore non vede più.
//
// `selezionabile` esclude le righe su cui l'azione non ha senso — l'admin
// connesso in Gestione Utenti, per esempio: la casella è spenta e dice perché.
const useSelezioneMultipla = (tutte = [], visibili = [], selezionabile = () => true) => {
  const [ids, setIds] = useState([]);

  // Potatura degli id spariti dall'elenco COMPLETO, non da quello filtrato:
  // filtrare non è deselezionare, e azzerare la selezione a ogni tasto premuto
  // nella casella di ricerca renderebbe la funzione inutilizzabile.
  const chiaviTutte = (tutte || []).map((r) => r.id).join("|");
  useEffect(() => {
    const esistenti = new Set((tutte || []).map((r) => r.id));
    setIds((prev) => {
      const puliti = prev.filter((id) => esistenti.has(id));
      return puliti.length === prev.length ? prev : puliti;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaviTutte]);

  const idsSelezionabili = (visibili || []).filter(selezionabile).map((r) => r.id);
  const tutteSelezionate = idsSelezionabili.length > 0 && idsSelezionabili.every((id) => ids.includes(id));

  const toggle = useCallback((id) => {
    setIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleTutte = useCallback(() => {
    setIds((prev) => {
      const tutteDentro = idsSelezionabili.length > 0 && idsSelezionabili.every((id) => prev.includes(id));
      if (tutteDentro) return prev.filter((id) => !idsSelezionabili.includes(id));
      return [...new Set([...prev, ...idsSelezionabili])];
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsSelezionabili.join("|")]);

  const pulisci = useCallback(() => setIds([]), []);

  return {
    ids,
    count: ids.length,
    isSel: (id) => ids.includes(id),
    toggle,
    toggleTutte,
    tutteSelezionate,
    pulisci,
  };
};

/** Casella in testata: seleziona/deseleziona tutte le righe VISIBILI. */
const CheckboxTutte = ({ selezione, titolo = "Seleziona tutte le righe visibili" }) => (
  <input
    type="checkbox"
    className="rounded"
    checked={selezione.tutteSelezionate}
    onChange={selezione.toggleTutte}
    title={titolo}
    onClick={(e) => e.stopPropagation()}
  />
);

/** Casella di riga. `motivo` spegne la casella e dice perché. */
const CheckboxRiga = ({ selezione, id, motivo }) => (
  <input
    type="checkbox"
    className="rounded"
    checked={selezione.isSel(id)}
    disabled={!!motivo}
    title={motivo || undefined}
    onChange={() => selezione.toggle(id)}
    onClick={(e) => e.stopPropagation()}
  />
);

/**
 * Barra delle azioni massive: compare solo con almeno una riga selezionata.
 *
 * Porta sempre il CONTEGGIO e il modo di annullare la selezione. Il conteggio
 * non è decorativo: è l'unica conferma che si sta per agire su quante righe si
 * crede, e in una tabella filtrata la selezione può contenere anche righe che
 * in quel momento non si vedono.
 */
const BarraAzioniMassive = ({ selezione, children, nomeSingolare = "riga", nomePlurale = "righe" }) => {
  if (!selezione || selezione.count === 0) return null;
  return (
    <div className="tactical-card p-3 mb-3 flex flex-wrap items-center gap-2 border-orange-500/40 bg-orange-500/5">
      <span className="text-sm text-orange-300 font-medium mr-1">
        {selezione.count} {selezione.count === 1 ? nomeSingolare : nomePlurale} selezionat{selezione.count === 1 ? "a" : "e"}
      </span>
      {children}
      <button className="btn btn-secondary btn-sm ml-auto" onClick={selezione.pulisci}>
        Annulla selezione
      </button>
    </div>
  );
};

/**
 * Esegue la stessa azione su più id e restituisce `{ok, ko}`.
 *
 * `allSettled` e non `all`: in un'azione massiva il fallimento di una riga non
 * deve fermare le altre, e soprattutto non deve nascondere quante sono andate a
 * buon fine. Il messaggio lo compone il chiamante, che sa come si chiamano le
 * cose che sta toccando; qui sta solo la parte che sarebbe identica quattro
 * volte — e che, contata male, direbbe "fatto" di un lavoro fatto a metà.
 */
const eseguiAzioneMassiva = async (ids, azione) => {
  const esiti = await Promise.allSettled((ids || []).map((id) => azione(id)));
  const ok = esiti.filter((e) => e.status === "fulfilled").length;
  return { ok, ko: esiti.length - ok };
};

// ── Campi obbligatori della Scheda 1 (raccolta dati disperso) ────────────────
// Sta QUI e non dentro SchedaDispersoPage perche' lo stesso elenco lo guarda il
// pallino di avviso accanto alla voce di menu. Due elenchi separati sarebbero
// divergiti alla prima aggiunta di un campo, e il pallino avrebbe cominciato a
// mentire: spento su una scheda incompleta e' peggio che non averlo, perche'
// insegna a fidarsi di un controllo che non c'e'.
//
// Accetta indifferentemente il `form` della pagina o il `dati` salvato: hanno le
// stesse chiavi. Restituisce i NOMI dei campi mancanti, non un booleano, cosi'
// il messaggio d'errore del salvataggio e il tooltip del pallino dicono la
// stessa cosa.
const campiObbligatoriScheda1 = (dati) => {
  const d = dati || {};
  const v = (val) => String(val ?? "").trim();
  const pua = (Array.isArray(d.pua_list) ? d.pua_list[0] : null) || {};
  return [
    [d.categoria_isrid, "Profilo ISRID"],
    [pua.in_data, "PUA — in data"],
    [pua.coordinate, "PUA — coordinate GPS"],
    [d.tempo_meteo, "Tempo meteo all'evento"],
    [d.cognome_nome, "Cognome e nome disperso"],
  ].filter(([val]) => !v(val)).map(([, n]) => n);
};

// Come sopra, ma partendo dalla RISPOSTA di GET /operazioni/{id}/scheda-disperso.
//
// Esiste perche' e' il punto esatto in cui ci si sbaglia, e lo sbaglio non si
// vede: la risposta e' la RIGA (`id`, `operazione_id`, `dati`, `chiusura`…) e i
// campi della scheda stanno dentro `dati`. Passando la riga intera,
// `campiObbligatoriScheda1` legge `riga.cognome_nome` — che non esiste — e li
// dichiara tutti mancanti per sempre: il pallino di avviso non si sarebbe mai
// spento, su nessuna scheda, per quanto la si compilasse. E' successo.
//
// Scheda assente: la rotta risponde `{}`, quindi `dati` e' undefined e mancano
// tutti — che e' la risposta giusta, non un caso da trattare a parte.
const campiObbligatoriDaScheda = (scheda) => campiObbligatoriScheda1(scheda?.dati);

// ── Il turno attivo copre ancora l'ora che e'? ───────────────────────────────
// Un turno di Giorno che supera il TRAMONTO va convertito in notturno; quello di
// Notte dura senza avviso fino all'ALBA, poi si torna al diurno. Finche'
// l'operatore non apre il turno nuovo, l'avviso resta.
//
// Restituisce il tipo in cui andrebbe convertito ("Giorno" | "Notte"), oppure ""
// quando non c'e' niente da segnalare. Due casi in cui tace di proposito:
//   - il nome del turno non finisce in _Giorno/_Notte (turno con nome libero, o
//     storico anteriore alla denominazione automatica): non c'e' niente da
//     confrontare;
//   - non c'e' nessuna fonte per alba e tramonto. `getNomeTurnoAuto` in quel caso
//     ripiega su "Giorno", che come DEFAULT per un nome va bene ma come ACCUSA
//     no: alle due di notte segnalerebbe di convertire in diurno un turno
//     notturno corretto.
const turnoDaConvertire = (nomeTurno, effemeridi = null, orarioSole = null) => {
  const tipoAttuale = /_(Giorno|Notte)$/.exec(String(nomeTurno || ""))?.[1];
  if (!tipoAttuale) return "";
  const conSole = !!(orarioSole?.alba && orarioSole?.tramonto);
  if (!conSole && !effemeridi) return "";
  const atteso = getNomeTurnoAuto(effemeridi, orarioSole).split("_").pop();
  return atteso === tipoAttuale ? "" : atteso;
};

// ── Totali superficie/lunghezza BATTUTE ──────────────────────────────────────
// Una missione conta UNA SOLA VOLTA anche se battuta piu' volte: si guarda se
// e' mai stata battuta (≥1 entry BATTUTA nello storico) o se lo stato corrente
// e' BATTUTA. Z → somma area_km2; L → somma lunghezza_m. P escluso.
const isBattuta = (m) =>
  m?.stato === 'BATTUTA' || (m?.storico || []).some(e => e.stato === 'BATTUTA');

const computeTotaliBattute = (missioni = []) => {
  let areaKm2 = 0, lunghezzaM = 0, nZone = 0, nLinee = 0;
  for (const m of missioni) {
    if (!isBattuta(m)) continue;
    if (m.tipo === 'Z' && m.area_km2 != null) { areaKm2 += m.area_km2; nZone++; }
    else if (m.tipo === 'L' && m.lunghezza_m != null) { lunghezzaM += m.lunghezza_m; nLinee++; }
  }
  return { areaKm2, lunghezzaM, nZone, nLinee };
};

// Superficie sempre mostrata in ETTARI (1 km² = 100 ha). L'area e' calcolata
// internamente in km² dalla geometria; qui convertiamo per la UI.
const formatSuperficie = (km2) => {
  if (!km2) return '0 ha';
  const ha = km2 * 100;
  const dec = ha < 10 ? 2 : ha < 1000 ? 1 : 0;
  return `${ha.toLocaleString('it-IT', { minimumFractionDigits: dec, maximumFractionDigits: dec })} ha`;
};

const formatLunghezza = (m) => {
  if (!m) return '0 m';
  if (m < 1000) return `${Math.round(m).toLocaleString('it-IT')} m`;
  return `${(m / 1000).toFixed(2)} km`;
};

// ── Soglia di rientro squadra (timer accountability) ─────────────────────────
// Minuti oltre i quali una squadra IN BATTUTA va segnalata: il timer sulla sua
// card diventa giallo. Vive sull'operazione (`soglia_rientro_minuti`, migr. 048)
// perche' il tempo ragionevole dipende da terreno e meteo di quella ricerca.
// 90 minuti = punto di partenza per una zona standard, da tarare sul campo.
const SOGLIA_RIENTRO_DEFAULT = 90;
const SOGLIA_RIENTRO_MIN = 5;
const SOGLIA_RIENTRO_MAX = 720;

/** Normalizza il valore digitato in un intero di minuti dentro i limiti. */
const normalizzaSogliaRientro = (v) => {
  const n = Math.round(Number(v));
  if (!isFinite(n) || n <= 0) return SOGLIA_RIENTRO_DEFAULT;
  return Math.min(SOGLIA_RIENTRO_MAX, Math.max(SOGLIA_RIENTRO_MIN, n));
};

// ── Aggiornamenti di sfondo silenziosi ───────────────────────────────────────
// I refetch periodici (rete di sicurezza del realtime, ogni 90s) rimpiazzavano lo
// stato con array NUOVI anche quando i dati erano identici: React ri-renderizzava
// e l'operatore vedeva la pagina "rinfrescarsi" mentre lavorava. Qui si confronta
// il contenuto e, se non e' cambiato nulla, si tiene lo STESSO riferimento: React
// non fa nulla e a schermo non si muove niente.
// JSON.stringify e' adeguato: le liste sono ordinate lato server e i payload sono
// piccoli (qualche decina di record); il costo e' trascurabile a questa cadenza.
const stessoContenuto = (a, b) => {
  try { return JSON.stringify(a) === JSON.stringify(b); }
  catch { return false; }   // cicli o valori non serializzabili: meglio aggiornare
};

/** Updater per setState: sostituisce solo se il contenuto e' davvero cambiato. */
const soloSeCambiato = (nuovi) => (prev) => (stessoContenuto(prev, nuovi) ? prev : nuovi);

export {
  chiaveMeteoCache, meteoInCache,
  SOGLIA_RIENTRO_DEFAULT, SOGLIA_RIENTRO_MIN, SOGLIA_RIENTRO_MAX, normalizzaSogliaRientro,
  stessoContenuto, soloSeCambiato,
  PrintHeader, FilterBar, FilterInput, FilterSelect,
  useDebounce, usePagination, Pagination,
  validateLatLon, parseCoordPair, filterCoord, validateCoordStr,
  getNomeTurnoAuto, turnoDaConvertire,
  campiObbligatoriScheda1, campiObbligatoriDaScheda,
  useSelezioneMultipla, CheckboxTutte, CheckboxRiga, BarraAzioniMassive, eseguiAzioneMassiva,
  computeTotaliBattute, formatSuperficie, formatLunghezza,
};
